/** Persisted master + per-layer audio settings. The Sounds section reads + writes
 *  this; main.ts loads it once at boot and applies to each sound class so the user's
 *  preferences survive page reloads. */

export type AudioLayerId =
  | 'engine'
  | 'windRumble'
  | 'whoosh'
  | 'teleport'
  | 'tire'
  | 'impact'
  | 'treeImpact'
  | 'ping';

export const AUDIO_LAYER_IDS: readonly AudioLayerId[] = [
  'engine',
  'windRumble',
  'whoosh',
  'teleport',
  'tire',
  'impact',
  'treeImpact',
  'ping',
] as const;

export interface LayerPref {
  volume: number; // 0..1
  muted: boolean;
}

export interface AudioPrefs {
  master: { volume: number; muted: boolean };
  layers: Record<AudioLayerId, LayerPref>;
}

export const AUDIO_LAYER_LABELS: Record<AudioLayerId, string> = {
  engine: 'Engine',
  windRumble: 'Wind rumble',
  whoosh: 'Whoosh',
  teleport: 'Teleport whoosh',
  tire: 'Tire / ground',
  impact: 'Crash impacts',
  treeImpact: 'Tree impacts',
  ping: 'Notifications',
};

export const DEFAULT_AUDIO_PREFS: AudioPrefs = {
  master: { volume: 0.7, muted: false },
  layers: {
    engine: { volume: 1.0, muted: false },
    windRumble: { volume: 1.0, muted: false },
    whoosh: { volume: 1.0, muted: false },
    teleport: { volume: 1.0, muted: false },
    tire: { volume: 1.0, muted: false },
    impact: { volume: 1.0, muted: false },
    treeImpact: { volume: 1.0, muted: false },
    ping: { volume: 1.0, muted: false },
  },
};

const STORAGE_KEY = 'roadworld.audioSettings.v1';

export function loadAudioPrefs(): AudioPrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return cloneDefaults();
    const parsed = JSON.parse(raw) as Partial<AudioPrefs>;
    // Merge over defaults so new layers added later still work for older saves.
    const out = cloneDefaults();
    if (parsed.master && typeof parsed.master === 'object') {
      if (typeof parsed.master.volume === 'number') out.master.volume = clamp01(parsed.master.volume);
      if (typeof parsed.master.muted === 'boolean') out.master.muted = parsed.master.muted;
    }
    if (parsed.layers && typeof parsed.layers === 'object') {
      const layers = parsed.layers as Partial<Record<AudioLayerId, Partial<LayerPref>>>;
      for (const id of AUDIO_LAYER_IDS) {
        const p = layers[id];
        if (!p) continue;
        if (typeof p.volume === 'number') out.layers[id].volume = clamp01(p.volume);
        if (typeof p.muted === 'boolean') out.layers[id].muted = p.muted;
      }
    }
    return out;
  } catch {
    return cloneDefaults();
  }
}

export function saveAudioPrefs(prefs: AudioPrefs): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Quota / unavailable — silently drop.
  }
}

function cloneDefaults(): AudioPrefs {
  return {
    master: { ...DEFAULT_AUDIO_PREFS.master },
    layers: {
      engine: { ...DEFAULT_AUDIO_PREFS.layers.engine },
      windRumble: { ...DEFAULT_AUDIO_PREFS.layers.windRumble },
      whoosh: { ...DEFAULT_AUDIO_PREFS.layers.whoosh },
      teleport: { ...DEFAULT_AUDIO_PREFS.layers.teleport },
      tire: { ...DEFAULT_AUDIO_PREFS.layers.tire },
      impact: { ...DEFAULT_AUDIO_PREFS.layers.impact },
      treeImpact: { ...DEFAULT_AUDIO_PREFS.layers.treeImpact },
      ping: { ...DEFAULT_AUDIO_PREFS.layers.ping },
    },
  };
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
